import { defineStore } from 'pinia'
import { backend_api_v2 } from '/src/utils/apiv2'
import bus from '/src/utils/event_bus'
import { useSessionStore } from '@/stores/session'
import { useTranslationsStore } from '@/stores/translations'

// The notification bell (docs/notification-bell-proposal.md). One timer for
// the whole app, started once from App.vue: the unread count is asked every
// minute and when the window gets focus, never by the button, which is
// mounted twice (rail and full menu) and on every page.
const POLL_MS = 60000
const PAGE = 30

export const useNotificationsStore = defineStore('notifications', {
    state: () => ({
        items: [],
        unread_count: 0,
        // null until the first answer: the first count of a session is not news.
        known_count: null,
        next_offset: null,
        loading: false,
        load_failed: false,
        panel_open: false,
        polling: false,
    }),
    actions: {
        startPolling() {
            if (this.polling) return
            this.polling = true
            const tick = () => this.loadUnreadCount()
            setInterval(tick, POLL_MS)
            window.addEventListener('focus', tick)
            // The timer keeps running and asks nothing while logged out, so
            // the next login needs no restart; only the last account's
            // notifications are dropped.
            bus.on('events/session/logout', () => {
                this.$reset()
                this.polling = true
            })
            tick()
        },
        async loadUnreadCount() {
            const session = useSessionStore()
            if (!session.authenticated || !session.user_object?.auth?.company) return
            try {
                const { data } = await backend_api_v2.get('mail/notifications/unread_count/')
                const count = data?.count || 0
                const grew = this.known_count !== null && count > this.known_count
                this.unread_count = count
                this.known_count = count
                if (grew) {
                    if (this.panel_open) this.loadFirstPage()
                    else this.announce(count)
                }
            } catch (e) {
                console.warn('Could not load the unread notifications count', e)
            }
        },
        // One toast per poll, never one per notification (§4.10).
        announce(count) {
            const t = useTranslationsStore().translate
            bus.emit('notification', {
                text: t('n_new_notifications').replace('{n}', count),
                color: 'accent',
                icon: 'mdi-bell-outline',
                timeout: 8000,
                action: { text: t('open'), handler: () => this.openPanel() },
            })
        },
        openPanel() {
            this.panel_open = true
        },
        async loadFirstPage() {
            this.items = []
            this.next_offset = 0
            return this.loadMore()
        },
        async loadMore() {
            if (this.next_offset === null || this.loading) return
            this.loading = true
            this.load_failed = false
            try {
                const { data } = await backend_api_v2.get(
                    `mail/notifications/?limit=${PAGE}&offset=${this.next_offset}`)
                const rows = data?.results || []
                const known = new Set(this.items.map(n => n.uuid))
                this.items.push(...rows.filter(n => !known.has(n.uuid)))
                this.next_offset = data?.next ? this.next_offset + rows.length : null
            } catch (e) {
                console.warn('Could not load the notifications', e)
                this.load_failed = true
            } finally {
                this.loading = false
            }
        },
        async markRead(n) {
            if (n.read) return
            n.read = true
            this.unread_count = Math.max(0, this.unread_count - 1)
            this.known_count = this.unread_count
            try {
                await backend_api_v2.post(`mail/notifications/${n.uuid}/read/`)
            } catch (e) {
                console.warn('Could not mark the notification read', e)
            }
        },
        async markAllRead() {
            this.items.forEach(n => { n.read = true })
            this.unread_count = 0
            this.known_count = 0
            try {
                await backend_api_v2.post('mail/notifications/read_all/')
            } catch (e) {
                console.warn('Could not mark the notifications read', e)
            }
        },
    },
})
